import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Input } from "@/components/ui/shadcn/input";
import { Label } from "@/components/ui/shadcn/label";
import {
  DateEnhancedQuote,
  DateEnhancedAcceptedQuote,
  QuoteStatus,
  AcceptedQuoteStatus,
} from "@/types/quotes";
import { DateEnhancedTrade } from "@/types/trades";
import { MultiSelect } from "@/components/ui/multiselect";
import { Checkbox } from "@/components/ui/shadcn/checkbox";
import { Search } from "lucide-react";
import { cn } from "@/lib/tailwind";
import { vmarketsAPI } from "@/services/api";
import { VirtualMarketResponseSchema } from "@/types/vmarkets";

type QuoteType =
  | DateEnhancedQuote
  | DateEnhancedAcceptedQuote
  | DateEnhancedTrade;

type StatusType = QuoteStatus | AcceptedQuoteStatus;

interface BaseFilteringProps {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  selectedVmarketIds?: number[];
  setSelectedVmarketIds?: Dispatch<SetStateAction<number[]>>;
  quotes: QuoteType[];
  withQuoteTypeBoxes?: boolean;
  isAcceptedQuotes?: boolean;
}

interface QuoteFilteringProps extends BaseFilteringProps {
  selectedStatuses?: QuoteStatus[];
  setSelectedStatuses?: Dispatch<SetStateAction<QuoteStatus[]>>;
}

interface AcceptedQuoteFilteringProps extends BaseFilteringProps {
  selectedStatuses?: AcceptedQuoteStatus[];
  setSelectedStatuses?: Dispatch<SetStateAction<AcceptedQuoteStatus[]>>;
}

type FilteringProps = QuoteFilteringProps | AcceptedQuoteFilteringProps;

// Define status arrays
const QUOTE_STATUSES: QuoteStatus[] = [
  QuoteStatus.ACTIVE,
  QuoteStatus.ACCEPTED,
  QuoteStatus.EXECUTED,
  QuoteStatus.EXPIRED,
  QuoteStatus.CANCELLED,
];
const ACCEPTED_QUOTE_STATUSES: AcceptedQuoteStatus[] = [
  AcceptedQuoteStatus.ACTIVE,
  AcceptedQuoteStatus.EXECUTED,
  AcceptedQuoteStatus.CANCELLED,
];

// Define status order mapping
const QUOTE_STATUS_ORDER: Record<QuoteStatus, number> = {
  [QuoteStatus.ACTIVE]: 1,
  [QuoteStatus.ACCEPTED]: 2,
  [QuoteStatus.EXECUTED]: 3,
  [QuoteStatus.EXPIRED]: 4,
  [QuoteStatus.CANCELLED]: 5,
};

const ACCEPTED_QUOTE_STATUS_ORDER: Record<AcceptedQuoteStatus, number> = {
  [AcceptedQuoteStatus.ACTIVE]: 1,
  [AcceptedQuoteStatus.EXECUTED]: 2,
  [AcceptedQuoteStatus.CANCELLED]: 3,
};

export function Filtering({
  searchTerm,
  setSearchTerm,
  selectedVmarketIds = [],
  setSelectedVmarketIds = () => {},
  selectedStatuses,
  setSelectedStatuses,
  quotes,
  withQuoteTypeBoxes = false,
  isAcceptedQuotes = false,
}: FilteringProps) {
  const [vmarkets, setVmarkets] = useState<VirtualMarketResponseSchema[]>([]);

  // Fetch available vmarkets
  useEffect(() => {
    const fetchVmarkets = async () => {
      try {
        const data = await vmarketsAPI.getVirtualMarkets();
        setVmarkets(data);
      } catch (error) {
        console.error("Failed to fetch vmarkets:", error);
      }
    };

    fetchVmarkets();
  }, []);

  const uniqueStatuses = Array.from(
    new Set([
      ...(isAcceptedQuotes ? ACCEPTED_QUOTE_STATUSES : QUOTE_STATUSES),
      ...quotes.map((quote) => {
        if ('status' in quote) {
          return quote.status;
        }
        return isAcceptedQuotes ? AcceptedQuoteStatus.ACTIVE : QuoteStatus.ACTIVE;
      }),
    ])
  ).sort((a, b) => {
    if (isAcceptedQuotes) {
      return ACCEPTED_QUOTE_STATUS_ORDER[a as AcceptedQuoteStatus] - ACCEPTED_QUOTE_STATUS_ORDER[b as AcceptedQuoteStatus];
    }
    return QUOTE_STATUS_ORDER[a as QuoteStatus] - QUOTE_STATUS_ORDER[b as QuoteStatus];
  });

  const handleStatusChange = (status: StatusType) => {
    if (!selectedStatuses || !setSelectedStatuses) return;

    if (isAcceptedQuotes) {
      const acceptedStatuses = selectedStatuses as AcceptedQuoteStatus[];
      const acceptedStatus = status as AcceptedQuoteStatus;
      if (acceptedStatuses.includes(acceptedStatus)) {
        (
          setSelectedStatuses as Dispatch<SetStateAction<AcceptedQuoteStatus[]>>
        )(acceptedStatuses.filter((s) => s !== acceptedStatus));
      } else {
        (
          setSelectedStatuses as Dispatch<SetStateAction<AcceptedQuoteStatus[]>>
        )([...acceptedStatuses, acceptedStatus]);
      }
    } else {
      const quoteStatuses = selectedStatuses as QuoteStatus[];
      const quoteStatus = status as QuoteStatus;
      if (quoteStatuses.includes(quoteStatus)) {
        (setSelectedStatuses as Dispatch<SetStateAction<QuoteStatus[]>>)(
          quoteStatuses.filter((s) => s !== quoteStatus)
        );
      } else {
        (setSelectedStatuses as Dispatch<SetStateAction<QuoteStatus[]>>)([
          ...quoteStatuses,
          quoteStatus,
        ]);
      }
    }
  };

  const isStatusSelected = (status: StatusType): boolean => {
    if (!selectedStatuses) return false;
    return isAcceptedQuotes
      ? (selectedStatuses as AcceptedQuoteStatus[]).includes(
          status as AcceptedQuoteStatus
        )
      : (selectedStatuses as QuoteStatus[]).includes(status as QuoteStatus);
  };

  return (
    <div className="bg-white dark:bg-zinc-950 rounded-lg border border-zinc-200 dark:border-zinc-800 px-5 py-4 mb-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        {/* Status Checkboxes */}
        {selectedStatuses && setSelectedStatuses && (
          <div className="w-full md:w-auto">
            <Label className="text-sm font-medium mb-1 block text-zinc-900 dark:text-zinc-50">
              Status
            </Label>
            <div className="flex flex-wrap gap-1.5 max-w-80">
              {uniqueStatuses.map((status) => (
                <div
                  key={status}
                  className={cn(
                    "flex items-center space-x-1.5 rounded-md border px-2 py-1.5 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900/50",
                    isStatusSelected(status)
                      ? "border-zinc-900/20 dark:border-zinc-300/20 bg-zinc-50 dark:bg-zinc-900/50"
                      : "border-zinc-200 dark:border-zinc-800"
                  )}
                >
                  <Checkbox
                    id={`status-${status}`}
                    checked={isStatusSelected(status)}
                    onCheckedChange={() => handleStatusChange(status)}
                    className="data-[state=checked]:bg-zinc-900/90 data-[state=checked]:border-zinc-900/90 h-3.5 w-3.5"
                  />
                  <label
                    htmlFor={`status-${status}`}
                    className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize cursor-pointer select-none text-zinc-900 dark:text-zinc-50"
                  >
                    {status}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vmarket Filter */}
        <div className="w-full md:w-80">
          <Label className="text-sm font-medium mb-1 block text-zinc-900 dark:text-zinc-50">
            Asset Pairs
          </Label>
          <MultiSelect
            options={vmarkets.map((vmarket) => ({
              value: vmarket.id.toString(),
              label: `${vmarket.base}/${vmarket.counter}`,
            }))}
            selected={selectedVmarketIds.map(String)}
            onChange={(values) => setSelectedVmarketIds(values.map(Number))}
            placeholder="Select..."
            className="w-full"
          />
        </div>

        {/* Search Bar */}
        <div className="w-full md:w-56">
          <Label className="text-sm font-medium mb-1 block text-zinc-900 dark:text-zinc-50">
            Search by client
          </Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-zinc-500" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
